import { Trans } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { RetiredBadge } from "@/components/admin/retired-badge";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Admin → Businesses → [businessId] → Branches.
 *
 * Port of src/app/admin/businesses/[id]/{page,branches-client}.tsx (legacy
 * NextJS) per D21 NO REINVENTION. Read-only branch list for a single
 * Business — editing happens through the public claim flow once onboarded.
 *
 * Endpoint: GET /api/admin/businesses/:id/branches → { business, branches }
 */

interface BusinessSummary {
  id: string;
  name: Record<string, string>;
  category: string;
  country: string;
  isClaimed: boolean;
}

interface Branch {
  id: string;
  branchName: string | null;
  subtype: string | null;
  legalName: string | null;
  taxId: string | null;
  registrationNo: string | null;
  addressStreet: string | null;
  addressCity: string;
  addressCounty: string | null;
  addressCountry: string;
  latitude: number | null;
  longitude: number | null;
  phone: string[];
  email: string | null;
  website: string | null;
  is24h: boolean;
  hasDelivery: boolean;
  hasHomeDraw: boolean;
  hasParking: boolean | null;
  status: string;
  lastScrapedAt: string | null;
  createdAt: string;
}

function displayName(name: Record<string, string> | null, fallback = "—"): string {
  if (!name) return fallback;
  return name.ro || name.en || Object.values(name).find((v) => v && !v.startsWith("_")) || fallback;
}

export const Route = createFileRoute("/_authenticated/businesses/$businessId")({
  component: BranchesPage,
});

function BranchesPage() {
  const { businessId } = Route.useParams();

  const query = useQuery({
    queryKey: ["admin", "businesses", "branches", businessId],
    queryFn: async (): Promise<{ business: BusinessSummary; branches: Branch[] } | null> => {
      try {
        return await apiFetch.get(`/api/admin/businesses/${businessId}/branches`);
      } catch (error) {
        if (error instanceof ApiError) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <div className="text-muted-foreground p-8 text-sm">
        <Trans>Loading branches…</Trans>
      </div>
    );
  }

  if (!query.data) {
    return (
      <div className="text-muted-foreground p-8 text-sm">
        <Trans>Business not found.</Trans>
      </div>
    );
  }

  const { business, branches } = query.data;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/businesses" className="text-muted-foreground hover:text-foreground text-xs">
          <Trans>← All businesses</Trans>
        </Link>
        <h1 className="text-foreground mt-1 text-2xl font-bold tracking-tight">
          {displayName(business.name)}
        </h1>
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-3 text-sm">
          <span className="capitalize">{business.category.replace(/_/g, " ")}</span>
          <span>{business.country}</span>
          {business.isClaimed ? (
            <Badge variant="default">
              <Trans>Claimed</Trans>
            </Badge>
          ) : (
            <Badge variant="outline">
              <Trans>Unclaimed</Trans>
            </Badge>
          )}
        </div>
      </div>

      <div className="text-muted-foreground text-sm tabular-nums">
        <Trans>{branches.length} branches</Trans>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Trans>Branch</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>City</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Address</Trans>
                  </TableHead>
                  <TableHead className="hidden xl:table-cell">
                    <Trans>Phone</Trans>
                  </TableHead>
                  <TableHead className="hidden xl:table-cell">
                    <Trans>Flags</Trans>
                  </TableHead>
                  <TableHead className="hidden xl:table-cell">
                    <Trans>Scraped</Trans>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                      <Trans>
                        No branches yet. Branches are created when scrapers ingest business data
                        with this CUI/EIK or matching brand name.
                      </Trans>
                    </TableCell>
                  </TableRow>
                ) : (
                  branches.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell>
                        <div className="font-medium">{b.branchName || "—"}</div>
                        {b.status === "retired" && (
                          <div className="mt-1">
                            <RetiredBadge />
                          </div>
                        )}
                        {b.subtype && (
                          <div className="text-muted-foreground text-xs">{b.subtype}</div>
                        )}
                        {b.legalName && (
                          <div className="text-muted-foreground text-xs">
                            {b.legalName}
                            {b.taxId && <span className="tabular-nums"> · CUI {b.taxId}</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {b.addressCity}
                        {b.addressCounty && (
                          <span className="text-muted-foreground text-xs">
                            {" "}
                            · {b.addressCounty}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="hidden text-sm lg:table-cell">
                        {b.addressStreet || "—"}
                      </TableCell>
                      <TableCell className="hidden text-sm tabular-nums xl:table-cell">
                        {b.phone?.[0] || "—"}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {b.is24h && (
                            <Badge variant="outline">
                              <Trans>24/7</Trans>
                            </Badge>
                          )}
                          {b.hasDelivery && (
                            <Badge variant="outline">
                              <Trans>Delivery</Trans>
                            </Badge>
                          )}
                          {b.hasHomeDraw && (
                            <Badge variant="outline">
                              <Trans>Home draw</Trans>
                            </Badge>
                          )}
                          {b.hasParking && (
                            <Badge variant="outline">
                              <Trans>Parking</Trans>
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden text-xs tabular-nums xl:table-cell">
                        {b.lastScrapedAt ? new Date(b.lastScrapedAt).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
